import React from "react";
import { imgUrl } from "../../lib/format";

/**
 * PDP left column — gallery + spec tiles + optional scientific specs +
 * chemistry-source citation. Structural extraction from ProductDetail.jsx.
 * No business logic; presentation only.
 */
export function ProductImages({ product }) {
  return (
    <div className="lg:col-span-7">
      <div className="relative aspect-square w-full mps-card overflow-hidden p-0">
        <img
          src={imgUrl(product.zoom_image_url || product.primary_image_url)}
          alt={product.image_alt_text || `${product.product_name} ${product.strength}`}
          className="absolute inset-0 h-full w-full object-contain object-center p-12 md:p-16 bg-brand-surface"
          loading="eager"
          decoding="async"
          width={1200}
          height={1200}
          data-testid="product-hero-image"
        />
        {product.image_match_status === "Missing Image" && (
          <div className="absolute top-4 left-4 text-[10px] font-semibold uppercase tracking-[0.14em] bg-amber-50 border border-amber-200 text-amber-800 px-3 py-1.5 rounded-full">
            Product image coming soon
          </div>
        )}
        <div className="absolute bottom-5 right-5 text-[10px] font-semibold uppercase tracking-[0.14em] text-brand-muted">
          {product.public_sku}
        </div>
      </div>

      {/* Spec strip — 2-3 primary specs; Purity omitted when undocumented */}
      <div className={`mt-6 grid gap-3 ${product.purity_percent ? "grid-cols-3" : "grid-cols-2"}`}>
        <SpecTile label="Net Peptide Mass" value={product.strength || "—"} testid="spec-strength" />
        {product.purity_percent && (
          <SpecTile label="Documented purity" value={product.purity_percent} testid="spec-purity-documented" />
        )}
        <SpecTile
          label="Storage"
          value={product.storage_conditions || product.storage_temperature || "−20 °C"}
          smaller
        />
      </div>

      {(product.molecular_formula || product.cas_number || product.molecular_weight || product.appearance) && (
        <div className="mt-3 grid grid-cols-2 gap-3" data-testid="scientific-specs">
          {product.molecular_formula && <SpecTile label="Molecular Formula" value={product.molecular_formula} />}
          {product.cas_number         && <SpecTile label="CAS Number"         value={product.cas_number} />}
          {product.molecular_weight   && <SpecTile label="Molecular Weight"   value={product.molecular_weight} />}
          {product.appearance         && <SpecTile label="Appearance"         value={product.appearance} smaller />}
        </div>
      )}

      <ChemistrySourceCitation product={product} />
    </div>
  );
}

function SpecTile({ label, value, testid, smaller = false }) {
  return (
    <div className="mps-card p-5">
      <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-brand-muted mb-1.5">{label}</div>
      <div className={`${smaller ? "text-sm" : "text-base"} font-mono text-brand-text leading-tight`} data-testid={testid}>
        {value}
      </div>
    </div>
  );
}

/**
 * Renders a public citation for CAS / molecular formula when — and only when —
 * an authoritative `cas_source` or `formula_source` attribution document was
 * captured during chemistry backfill (Phase 20/21). Silently omits absent or
 * unverified sources so no unattributed values are ever surfaced.
 */
function ChemistrySourceCitation({ product }) {
  const cas = product.cas_source;
  const formula = product.formula_source;
  if (!cas && !formula) return null;

  const rows = [];
  if (cas && cas.org) {
    rows.push({
      label: "CAS attribution",
      org: cas.org,
      title: cas.source_id || cas.title || "",
      url: cas.url,
      accessed: cas.date_accessed || cas.accessed_at || product.chemistry_reviewed_at || null,
    });
  }
  if (formula && formula.org) {
    rows.push({
      label: "Molecular formula attribution",
      org: formula.org,
      title: formula.source_id || formula.title || "",
      url: formula.url,
      accessed: formula.date_accessed || formula.accessed_at || product.chemistry_reviewed_at || null,
    });
  }
  if (rows.length === 0) return null;

  return (
    <div className="mt-4 mps-card p-5" data-testid="chemistry-source-citation">
      <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-brand-muted mb-2">
        Chemistry data sources
      </div>
      <ul className="space-y-1.5 text-[12px] text-brand-muted font-mono leading-relaxed">
        {rows.map((r, i) => (
          <li key={r.label} data-testid={`chemistry-source-row-${i}`}>
            <span className="text-brand-navy">{r.label}:</span>{" "}
            <span>{r.org}</span>
            {r.title ? <span> · {r.title}</span> : null}
            {r.url ? (
              <>
                {" · "}
                <a
                  href={r.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-accent hover:underline"
                  data-testid={`chemistry-source-link-${i}`}
                >
                  view
                </a>
              </>
            ) : null}
            {r.accessed ? <span> · accessed {String(r.accessed).slice(0, 10)}</span> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
